import { z } from 'zod'
import { ianaTimeZone, isoDate, isoDateTime, uuid } from './common.js'

export const tripStatus = z.enum(['planning', 'ticketed', 'travelling', 'complete'])
export type TripStatus = z.infer<typeof tripStatus>

const tripFields = {
  title: z.string().trim().min(1).max(200),
  startDate: isoDate,
  endDate: isoDate,
  /**
   * The trip's own zone, e.g. 'Europe/Paris'. Reservation times render in this
   * zone — the zone of the place you are standing in — never in the viewer's.
   * A traveller checking tomorrow's train time from home must see the time the
   * platform board will show.
   */
  timezone: ianaTimeZone,
  /**
   * Consulate, insurance line, the advisor's own mobile. Rendered above the
   * itinerary rather than inside it: somebody reaching for this is having a bad
   * day and should not have to scroll past Tuesday to find it.
   */
  emergencyInfo: z.string().max(5_000).nullish(),
}

const orderedDates = <T extends { startDate: string; endDate: string }>(v: T, ctx: z.RefinementCtx) => {
  if (v.endDate < v.startDate) {
    ctx.addIssue({
      code: 'custom',
      path: ['endDate'],
      message: 'end date cannot precede start date',
    })
  }
}

export const createTripRequest = z
  .object({ ...tripFields, status: tripStatus.optional() })
  .superRefine(orderedDates)
export type CreateTripRequest = z.infer<typeof createTripRequest>

export const updateTripRequest = z
  .object({ ...tripFields, status: tripStatus, isTemplate: z.boolean() })
  .partial()
  .superRefine((v, ctx) => {
    if (v.startDate && v.endDate) orderedDates({ startDate: v.startDate, endDate: v.endDate }, ctx)
  })
export type UpdateTripRequest = z.infer<typeof updateTripRequest>

export const trip = z.object({
  id: uuid,
  ...tripFields,
  emergencyInfo: z.string().nullable(),
  status: tripStatus,
  /**
   * A saved starting point — "my standard 5 days in Paris" — rather than
   * somebody's journey. Kept apart from `status` deliberately: status is a
   * position on the road to departure and this is not on that road. See
   * `0025_trip_templates.sql` for the whole argument.
   *
   * A template's own dates are scaffolding. Only their spacing survives being
   * used: the new trip starts where the advisor says and runs as long as the
   * template does.
   */
  isTemplate: z.boolean(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type Trip = z.infer<typeof trip>

/**
 * Copying a whole trip.
 *
 * `startDate` is the useful half: the same itinerary run three months later
 * needs every day and every booking moved, and doing that by hand is the reason
 * nobody reuses a trip. Omit it and the copy keeps the original's dates.
 */
export const duplicateTripRequest = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  startDate: isoDate.optional(),
})
export type DuplicateTripRequest = z.infer<typeof duplicateTripRequest>

/**
 * Starting a new trip from a template.
 *
 * Both fields are required, where `duplicateTripRequest` makes both optional,
 * and the difference is the point. A duplicate of a real trip has a name and
 * dates worth falling back to; a template has neither. "Paris — 5 dias" is what
 * the thing is called in her library, not what a client's trip is called, and a
 * template's dates exist only to space its days apart.
 */
export const createTripFromTemplateRequest = z.object({
  templateId: uuid,
  title: z.string().trim().min(1).max(200),
  startDate: isoDate,
})
export type CreateTripFromTemplateRequest = z.infer<typeof createTripFromTemplateRequest>
